/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import React, { useMemo, useEffect, useState } from 'react';
import PlatformList from '../../fleet/PlatformList';
import { PlatformListGroup } from '../../../shared/entities/platformListGroup';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  ThemeProvider,
  StyledEngineProvider,
  CssBaseline,
  Box,
  Theme,
  Typography,
  IconButton,
  Button,
  adaptV4Theme,
} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { createTheme } from '@mui/material/styles';
import { KU_PLUS_KA_DRAWER_WIDTH_PX } from '../../fleet/FleetMap';
import {
  clearSelectedDatapathUuidInPlatformList,
  collapseTerminalInPlatformList,
  expandTerminalInPlatformList,
  hideTerminalsInPlatformList,
  setCollapsedTerminalsInPlatformList,
  setSelectedDatapathUuidInPlatformList,
  showTerminalsInPlatformList,
  setSelectedTerminalTabIndex,
  openPlatformListDrawer,
  closePlatformListDrawer,
  setPlatformListSortField,
  setPlatformListSortDirection,
  closeDetailsPanel,
  openDetailsPanel,
} from '../../../store/ui/actions';
import { setMapZoomLatLng, TRANSPORT_MONITORING_MAP_ID } from '../../../store/maps/actions';
import Terminal from '../../../shared/entities/terminal';
import {
  selectHiddenTerminalsList,
  selectSelectedDatapathUuidInPlatformList,
  selectSelectedTerminalTabIndex,
  selectPlatformListSortDirection,
  selectSpaceVehicleTerminals,
  selectCollapsedTerminalsInPlatformList,
  selectIsInitialTerminalsRequestFinished,
  selectSpaceVehicleListItemsUnfilteredAndUnsorted,
  TerminalListItem,
  selectTerminalFilters,
  selectPlatformListDrawerOpen,
  selectGroupedSpaceVehicleListItems,
  selectGroupedSpaceVehicles,
} from '../../../store';
import { SortDirection } from '../../../store/ui/reducer';
import { panToTerminalOnMap } from '../../../utils/MapUtils';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ResourceFilter } from '../../common/table/types';
import { isOnline } from '../../../utils/ConnectionStatusUtils';
import appTheme from '../../common/theme/appTheme.json';
import { clearFlightPath } from '../../../store/terminals/actions';
import { PortalsEnum } from '../../../shared/entities/portal';

const TOOLBAR_HEIGHT_PX = 153;

const baseTheme = createTheme(
  adaptV4Theme({
    typography: {
      fontFamily: `'Source Sans Pro', sans-serif`,
    },
    palette: {
      primary: { main: '#5EA215' },
      secondary: { main: '#61DED8' },
      background: {
        paper: '#202E39',
        default: '#142431',
      },
      text: { primary: '#ffffff' },
      action: { hover: '#0D1418', selected: '#000000' },
      divider: '#465967',
    },
  }),
);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bulletChar: {
      fontFamily: 'Uni Neue Light',
    },
    visibilityToggleContainer: {
      right: 0,
      paddingRight: '8px',
    },
    visibilityIcon: {
      maxHeight: '18px',
      maxWidth: '18px',
      color: '#C3CDD5',
    },
    collapseIcon: {
      maxHeight: '18px',
      maxWidth: '18px',
      color: '#C3CDD5',
    },
  }),
);

/**
 * Equivalent to PlatformListContainer.tsx but for Transport Monitoring page
 */
export const SpaceVehicleListContainer = () => {
  // Redux Actions
  const dispatch = useDispatch();

  // Theme and Style
  const classes = useStyles();

  const { height: windowHeight } = useWindowDimensions();

  // Redux Selecters
  const spaceVehicleListDrawerOpen = useSelector(selectPlatformListDrawerOpen, shallowEqual);
  const hiddenSpaceVehicles: string[] = useSelector(selectHiddenTerminalsList, shallowEqual);
  const selectedSpaceVehicleInList = useSelector(selectSelectedDatapathUuidInPlatformList, shallowEqual);
  const selectedSpaceVehicleTabIndex = useSelector(selectSelectedTerminalTabIndex, shallowEqual);
  const spaceVehicleListSortDirection = useSelector(selectPlatformListSortDirection, shallowEqual);
  const spaceVehicleMap = useSelector(selectSpaceVehicleTerminals, shallowEqual);
  const collapsedSpaceVehiclesInList = useSelector(selectCollapsedTerminalsInPlatformList, shallowEqual);
  const isInitialTerminalsRequestFinished = useSelector(selectIsInitialTerminalsRequestFinished, shallowEqual);
  const spaceVehicleListItems = useSelector(selectSpaceVehicleListItemsUnfilteredAndUnsorted, shallowEqual);
  const transportMonitoringFilters = useSelector(selectTerminalFilters, shallowEqual);
  const groupedSpaceVehicleListItems = useSelector(selectGroupedSpaceVehicleListItems, shallowEqual);
  const groupedSpaceVehicles = useSelector(selectGroupedSpaceVehicles, shallowEqual);

  const spaceVehicleListSortField = 'terminalDisplayName';

  const terminalListHeader = useMemo(
    () => (
      <Box height="48px" width={KU_PLUS_KA_DRAWER_WIDTH_PX} position="relative">
        <Box position="absolute" flexGrow={1} left="16px" top="21.5px">
          <Typography component="div">
            <Box fontWeight={300} fontSize="20px" lineHeight="20px">
              <span data-test="platform__list__terminal__count__header">
                {`All Space Vehicles (${getNumberOfTerminals(
                  spaceVehicleListItems,
                  spaceVehicleMap,
                  transportMonitoringFilters,
                )}`}
              </span>
              <span className={classes.bulletChar}>{` • `}</span>
              <span data-test="platform__list__terminal__online__count">
                {`${getNumberOfOnlineTerminals(
                  spaceVehicleListItems,
                  spaceVehicleMap,
                  transportMonitoringFilters,
                )} Online)`}
              </span>
            </Box>
          </Typography>
        </Box>
        <Box position="absolute" top="8px" right="8px">
          <IconButton
            data-test="close-terminal-list-drawer-button"
            onClick={() => {
              dispatch(closePlatformListDrawer());
            }}
          >
            <ChevronLeftIcon sx={{ color: 'white' }} fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    ),
    [spaceVehicleListItems, dispatch, transportMonitoringFilters, spaceVehicleMap, classes.bulletChar],
  );

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={baseTheme}>
        <CssBaseline />
        {spaceVehicleListDrawerOpen && isInitialTerminalsRequestFinished ? (
          <Box position="absolute" width={KU_PLUS_KA_DRAWER_WIDTH_PX}>
            {terminalListHeader}
            <PlatformList
              /* 
                Props 
              */
              // List Settings
              listHeightPx={windowHeight - TOOLBAR_HEIGHT_PX}
              // List Primary Data
              terminalListItems={groupedSpaceVehicleListItems}
              // List Redux State
              hiddenDatapathUuids={hiddenSpaceVehicles}
              collapsedDatapathUuids={collapsedSpaceVehiclesInList}
              selectedDatapathUuid={selectedSpaceVehicleInList}
              selectedTerminalTabIndex={selectedSpaceVehicleTabIndex}
              terminalListSortDirection={spaceVehicleListSortDirection}
              terminalListSortField={spaceVehicleListSortField}
              // List Supplemental Data
              terminalMap={spaceVehicleMap}
              terminals={groupedSpaceVehicles}
              hideTtho={true}
              /*
                Click Handlers
              */
              onCollapseGroupClickedHandler={(event: React.MouseEvent, groupName: string) => {
                event.stopPropagation();
                dispatch(
                  setCollapsedTerminalsInPlatformList(
                    groupedSpaceVehicles[groupName].reduce(
                      (prev: any, terminal: Terminal) => ({ ...prev, [terminal.datapathUuid]: true }),
                      { ...collapsedSpaceVehiclesInList },
                    ),
                  ),
                );
              }}
              onExpandGroupClickedHandler={(event: React.MouseEvent, groupName: string) => {
                event.stopPropagation();
                dispatch(
                  setCollapsedTerminalsInPlatformList(
                    groupedSpaceVehicles[groupName].reduce(
                      (prev: any, terminal: Terminal) => ({ ...prev, [terminal.datapathUuid]: false }),
                      { ...collapsedSpaceVehiclesInList },
                    ),
                  ),
                );
              }}
              onHideGroupClickedHandler={(event: React.MouseEvent, groupName: string): void => {
                event.stopPropagation();
                dispatch(
                  hideTerminalsInPlatformList(
                    groupedSpaceVehicles[groupName].map((spaceVehicle: Terminal) => spaceVehicle.datapathUuid),
                  ),
                );
                dispatch(clearFlightPath(PortalsEnum.ISAN_PORTAL));
                dispatch(clearSelectedDatapathUuidInPlatformList());
                dispatch(closeDetailsPanel());
              }}
              onShowGroupClickedHandler={(event: React.MouseEvent, groupName: string): void => {
                event.stopPropagation();
                // dispatch(showAllTerminalsInPlatformList());
                dispatch(
                  showTerminalsInPlatformList(
                    groupedSpaceVehicles[groupName].map((spaceVehicle: Terminal) => spaceVehicle.datapathUuid),
                  ),
                );
              }}
              onRowClickedHandler={(datapathUuid: string, index: number): void => {
                dispatch(setSelectedTerminalTabIndex(index));
                if (datapathUuid === selectedSpaceVehicleInList) {
                  setTimeout(() => {
                    panToTerminalOnMap(
                      datapathUuid,
                      spaceVehicleMap,
                      (latLngObj: google.maps.LatLngLiteral) => {
                        dispatch(setMapZoomLatLng(TRANSPORT_MONITORING_MAP_ID)(latLngObj));
                      },
                      hiddenSpaceVehicles,
                    );
                  });
                } else {
                  dispatch(setSelectedDatapathUuidInPlatformList(datapathUuid));
                  setTimeout(() => {
                    panToTerminalOnMap(
                      datapathUuid,
                      spaceVehicleMap,
                      (latLngObj: google.maps.LatLngLiteral) => {
                        dispatch(setMapZoomLatLng(TRANSPORT_MONITORING_MAP_ID)(latLngObj));
                      },
                      hiddenSpaceVehicles,
                    );
                  });
                  dispatch(openDetailsPanel());
                  dispatch(clearFlightPath(PortalsEnum.ISAN_PORTAL));
                  // TODO - Removed 'dispatch(getFlightPathOnce())'. Replace with a newly created Space Vehicle - equivalent version for Flight Path whenever we decide to implement SV flight path
                }
              }}
              onRowCollapseClickedHandler={(datapathUuid: string): void => {
                const datapathUuidsInGroup = [
                  datapathUuid,
                  ...((spaceVehicleMap[datapathUuid] || {}).datapathUuidsOnAircraft || []),
                ];
                if (
                  datapathUuidsInGroup.find(
                    (datapathUuidInGroup: string) => collapsedSpaceVehiclesInList[datapathUuidInGroup],
                  )
                ) {
                  datapathUuidsInGroup.forEach((datapathUuidInGroup: string) => {
                    dispatch(expandTerminalInPlatformList(datapathUuidInGroup));
                  });
                } else {
                  datapathUuidsInGroup.forEach((datapathUuidInGroup: string) => {
                    dispatch(collapseTerminalInPlatformList(datapathUuidInGroup));
                  });
                }
              }}
              onRowHideClickedHandler={(datapathUuid: string): void => {
                const datapathUuids = [datapathUuid, ...((spaceVehicleMap[datapathUuid] || {}).datapathUuidsOnAircraft || [])];
                if (hiddenSpaceVehicles.includes(datapathUuid)) {
                  dispatch(showTerminalsInPlatformList(datapathUuids));
                } else {
                  if (selectedSpaceVehicleInList && datapathUuids.includes(selectedSpaceVehicleInList)) {
                    dispatch(clearFlightPath(PortalsEnum.ISAN_PORTAL));
                    dispatch(clearSelectedDatapathUuidInPlatformList());
                    dispatch(closeDetailsPanel());
                  }
                  dispatch(hideTerminalsInPlatformList(datapathUuids));
                }
              }}
              onSortColumnHeaderClickedHandler={(
                sortField: string,
                sortDirection: SortDirection,
                groupName: string,
              ): void => {
                dispatch(setPlatformListSortField(sortField));
                dispatch(setPlatformListSortDirection(groupName as PlatformListGroup, sortDirection));
              }}
              onRowSubItemClickedHandler={(datapathUuid: string, tabIndex: number | undefined): void => {
                dispatch(setSelectedTerminalTabIndex(tabIndex));
                if (datapathUuid === selectedSpaceVehicleInList) {
                  setTimeout(() => {
                    panToTerminalOnMap(
                      datapathUuid,
                      spaceVehicleMap,
                      (latLngObj: google.maps.LatLngLiteral) => {
                        dispatch(setMapZoomLatLng(TRANSPORT_MONITORING_MAP_ID)(latLngObj));
                      },
                      hiddenSpaceVehicles,
                    );
                  });
                } else {
                  dispatch(setSelectedDatapathUuidInPlatformList(datapathUuid));
                  setTimeout(() => {
                    panToTerminalOnMap(
                      datapathUuid,
                      spaceVehicleMap,
                      (latLngObj: google.maps.LatLngLiteral) => {
                        dispatch(setMapZoomLatLng(TRANSPORT_MONITORING_MAP_ID)(latLngObj));
                      },
                      hiddenSpaceVehicles,
                    );
                  });
                  dispatch(openDetailsPanel());
                  dispatch(clearFlightPath(PortalsEnum.ISAN_PORTAL));
                  // TODO - Removed 'dispatch(getFlightPathOnce())'. Replace with a newly created Space Vehicle - equivalent version for Flight Path whenever we decide to implement SV flight path
                }
              }}
              openListDrawer={openPlatformListDrawer}
              listDrawerOpen={spaceVehicleListDrawerOpen}
            />
          </Box>
        ) : (
          <Box maxWidth="32px" position="absolute">
            <Button
              color="primary"
              data-test="open-terminal-list-drawer-button"
              disableRipple
              sx={{
                minHeight: `calc(100vh - 108px)`,
                minWidth: '32px',
                maxWidth: '32px',
                alignItems: 'start',
                paddingTop: '22px',
                '&:hover': {
                  backgroundColor: appTheme.buttonHoverRippleBackgroundColor,
                },
              }}
              onClick={() => {
                dispatch(openPlatformListDrawer());
              }}
            >
              <ChevronRightIcon sx={{ color: appTheme.primaryButtonTextColor }} fontSize="small" />
            </Button>
          </Box>
        )}
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

// Counts the number of terminals from the List Item groups as each list item group has 1 or more terminals in it
const getNumberOfTerminals = (
  terminalListItems: TerminalListItem[],
  terminalMap: { [datapathUuid: string]: Terminal },
  fleetViewFilters: ResourceFilter[],
) => {
  return terminalListItems.reduce(
    (numberOfTerminals: number, currentTerminalListItem: TerminalListItem) =>
      numberOfTerminals +
      currentTerminalListItem.datapathUuidsInGroup.filter((datapathUuid: string) =>
        fleetViewFilters.every(({ key, value }) => (terminalMap[datapathUuid] || {})[key] === value),
      ).length,
    0,
  );
};

const getNumberOfOnlineTerminals = (
  terminalListItems: TerminalListItem[],
  terminalMap: { [datapathUuid: string]: Terminal },
  fleetViewFilters: ResourceFilter[],
) => {
  return terminalListItems.reduce(
    (numberOfTerminals: number, currentTerminalListItem: TerminalListItem) =>
      numberOfTerminals +
      currentTerminalListItem.datapathUuidsInGroup.filter(
        (datapathUuid: string) =>
          fleetViewFilters.every(({ key, value }) => (terminalMap[datapathUuid] || {})[key] === value) &&
          isOnline(terminalMap[datapathUuid].connectivityStatus),
      ).length,
    0,
  );
};

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

export function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
}
